import { differenceBy, isNaN } from 'lodash/fp';
import { string } from 'fp-ts';
import { UseFormReset } from 'react-hook-form';
import { createAction, lt, retryPromise, showSuccessAlert } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import {
    MultipleAvailabilityBlockUpdateParameters,
    ReleaseStrategy,
    ServiceAvailabilityUpdateParameters,
} from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { DirtyFields } from '@mews-commander/form';
import { dateTimeToUtc, getAppDateTimeNow } from '@mews-commander/utils';
import { updateServiceAvailability } from '@mews-commander/legacy-entities/src/actions/services';
import { getAvailabilityAdjustmentsByAvailabilityBlockId } from '@mews-commander/legacy-entities/src/ducks/availabilityAdjustments';
import { getAvailabilityBlock } from '@mews-commander/legacy-entities/src/ducks/availabilityBlocks';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { updateAvailabilityBlocks } from '@mews-commander/legacy-entities/src/actions/availabilityBlocks';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import {
    AVAILABILITY_ADJUSTMENT_UPDATE_STARTED,
    AVAILABILITY_ADJUSTMENT_UPDATE_FAILED,
    AVAILABILITY_ADJUSTMENT_UPDATE_SUCCEEDED,
    FORM_MODE_CHANGED,
    MONTH_CHANGED,
    WAS_RELEASE_OVERRIDEN,
} from '../actionTypes';
import { AvailabilityAdjustmentFormValues } from '../types';
import {
    getBlockAdjustmentAvailableNumber,
    cachedAvailabilityAdjustmentSelector,
    getCurrentEnterpriseMinimalOperationEditableUtc,
} from '../selectors';
import { ReleasedDateFormValues } from '../components/Dialogs/ReleaseDateDialog/ReleasedDateContent';
import { getReleaseStrategy } from '../../availabilityBlockEntityForm/utils';
import { fetchAvailabilities } from './availabilities';

export const changeFormMode = (isEditMode: boolean) => createAction(FORM_MODE_CHANGED, isEditMode);

export const changeMonth = (month: number) => createAction(MONTH_CHANGED, month);

export const isFieldDirty = (
    dirtyFields: DirtyFields<AvailabilityAdjustmentFormValues>,
    rowIndex: number,
    columnIndex: number
) => {
    const dirtyItem = dirtyFields.fieldArrayAvailability?.[rowIndex];

    return !!(
        dirtyItem &&
        ((dirtyItem.paxCount && (dirtyItem.paxCount.personCount || dirtyItem.paxCount?.unitCount)) ||
            dirtyItem.resourceCategoryId ||
            dirtyItem.availabilities?.[columnIndex]?.adjustmentValue?.value)
    );
};

export const updateAvailabilities =
    (
        values: AvailabilityAdjustmentFormValues,
        dirtyFields: DirtyFields<AvailabilityAdjustmentFormValues>,
        serviceId: string,
        availabilityBlockId: string,
        reset: UseFormReset<AvailabilityAdjustmentFormValues>
    ): CommanderThunkAction =>
    (dispatch, getState, { featureFlagService }) => {
        dispatch(createAction(AVAILABILITY_ADJUSTMENT_UPDATE_STARTED));
        const minimalOperationalEditableUtc = getCurrentEnterpriseMinimalOperationEditableUtc(getState());
        const availabilityAdjustments = getAvailabilityAdjustmentsByAvailabilityBlockId(getState(), {
            availabilityBlockId,
        });

        const getAdjustmentsByKeys = cachedAvailabilityAdjustmentSelector(availabilityAdjustments);

        const differenceByResourceCategory = differenceBy(
            'resourceCategoryId',
            availabilityAdjustments,
            values.fieldArrayAvailability
        );
        const oldAdjustmentsToBeDeleted = differenceByResourceCategory.map(
            ({ startUtc, endUtc, resourceCategoryId }) => ({
                adjustmentValue: { value: null },
                availabilityBlockId: { value: availabilityBlockId },
                lastTimeUnitStartUtc: endUtc,
                firstTimeUnitStartUtc: startUtc,
                resourceCategoryId,
            })
        );

        const availabilityArray: Array<ServiceAvailabilityUpdateParameters> = values.fieldArrayAvailability.flatMap(
            ({ resourceCategoryId, availabilities, paxCount }, i) =>
                availabilities.reduce(
                    (acc: Array<ServiceAvailabilityUpdateParameters>, { adjustmentValue, dateTimeUtcForm }, j) => {
                        if (dateTimeUtcForm <= minimalOperationalEditableUtc) {
                            return acc;
                        }

                        const value = string.isString(adjustmentValue.value)
                            ? parseInt(adjustmentValue.value, 10)
                            : adjustmentValue.value;

                        const initialAvailabilityAdjustment = getAdjustmentsByKeys({
                            availabilityBlockId,
                            resourceCategoryId,
                            startUtc: dateTimeUtcForm,
                        });

                        const adjustmentUpdateValue = value && !isNaN(value) ? -value : null;

                        if (isFieldDirty(dirtyFields, i, j)) {
                            acc.push({
                                adjustmentValue: { value: adjustmentUpdateValue },
                                resourceCategoryId,
                                firstTimeUnitStartUtc: dateTimeUtcForm,
                                lastTimeUnitStartUtc: dateTimeUtcForm,
                                availabilityBlockId: {
                                    value: availabilityBlockId,
                                },
                                releaseOverrideUtc: initialAvailabilityAdjustment?.releaseOverrideUtc,
                                paxCounts: featureFlagService.hasFlag(FeatureFlag.occupancyInBlocksEnabled)
                                    ? [
                                          {
                                              ...paxCount,
                                              unitCount: adjustmentUpdateValue ? Math.abs(adjustmentUpdateValue) : 0,
                                          },
                                      ]
                                    : undefined,
                            });
                        }

                        return acc;
                    },
                    [] as Array<ServiceAvailabilityUpdateParameters>
                )
        );

        const availabilityUpdates = [...availabilityArray, ...oldAdjustmentsToBeDeleted];
        return dispatch(updateServiceAvailability({ serviceId, availabilityUpdates, availabilityBlockId }))
            .then(async () => {
                const state = getState();
                const availabilityBlock = getAvailabilityBlock(state, { id: availabilityBlockId });
                const startUtc = availabilityBlock?.startUtc;
                const endUtc = availabilityBlock?.endUtc;

                if (!startUtc || !endUtc) {
                    throw Error('StartUtc or EndUtc in the availability block is missing.');
                }

                return dispatch(fetchAvailabilities({ serviceId, startUtc, endUtc }));
            })
            .then(() => {
                dispatch(createAction(AVAILABILITY_ADJUSTMENT_UPDATE_SUCCEEDED));
                dispatch(changeFormMode(false));
                reset(values);
            })
            .then(() => showSuccessAlert({ description: lt('SaveSuccessMessage') }))
            .catch(() => {
                dispatch(createAction(AVAILABILITY_ADJUSTMENT_UPDATE_FAILED));
            });
    };

const updateAvailabilityBlock =
    ({
        requestBody,
        availabilityBlockId,
        serviceId,
        checkForReleasedAvailability = false,
    }: {
        requestBody: MultipleAvailabilityBlockUpdateParameters;
        availabilityBlockId: string;
        serviceId: string;
        checkForReleasedAvailability?: boolean;
    }): CommanderThunkAction =>
    async (dispatch, getState) => {
        await dispatch(updateAvailabilityBlocks(requestBody));
        try {
            await retryPromise(
                async () => {
                    const availabilityBlock = getAvailabilityBlock(getState(), { id: availabilityBlockId });
                    if (!availabilityBlock) throw Error('StartUtc or EndUtc in the availability block is missing.');

                    const { startUtc, endUtc } = availabilityBlock;

                    await dispatch(fetchAvailabilities({ serviceId, startUtc, endUtc }));
                    if (checkForReleasedAvailability) {
                        const available = getBlockAdjustmentAvailableNumber(getState(), {
                            availabilityBlockId,
                        });

                        if (available !== 0) throw new Error('Released availability fetch was unsuccessful.');
                    }
                },
                { minTimeout: 2000, attempts: 5 }
            );
        } finally {
            showSuccessAlert({
                description: lt('SaveSuccessMessage'),
            });
        }
    };

export const updateAvailabilityBlockReleasedUtc =
    (
        { date, time, blockReleaseStrategy, offsetDays, offsetTime }: ReleasedDateFormValues,
        id: string,
        serviceId: string
    ): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(state);
        const localizationInfo = getCurrentDateTimeLocalizationInfo(state);

        const releaseStrategy = getReleaseStrategy({
            date,
            time,
            blockReleaseStrategy,
            offsetDays,
            offsetTime,
            timezone,
            localizationInfo,
        });

        const requestBody = {
            availabilityBlocks: [
                {
                    availabilityBlockId: id,
                    releaseStrategy: { value: { releaseStrategy } },
                },
            ],
        };
        return dispatch(updateAvailabilityBlock({ requestBody, availabilityBlockId: id, serviceId }));
    };

export const releaseAll =
    (id: string, serviceId: string): CommanderThunkAction =>
    (dispatch, getState, { appClockService }) => {
        const timezone = getCurrentEnterpriseIanaTimeZoneIdentifier(getState());
        const requestBody = {
            availabilityBlocks: [
                {
                    availabilityBlockId: id,
                    releaseStrategy: {
                        value: {
                            releaseStrategy: {
                                discriminator: ReleaseStrategy.FixedReleaseUtc,
                                value: dateTimeToUtc(getAppDateTimeNow({ appClockService, timezone })),
                            },
                        },
                    },
                },
            ],
        };
        return dispatch(
            updateAvailabilityBlock({
                requestBody,
                availabilityBlockId: id,
                serviceId,
                checkForReleasedAvailability: true,
            })
        );
    };

export const setReleaseOverriden = () => createAction(WAS_RELEASE_OVERRIDEN);
